import type { FC, ReactNode } from 'react'

import Router from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { Wordmark } from '@/components/generals/wordmark'

type Phase = 'idle' | 'cover' | 'reveal'

/* Keep in step with the .blitz-curtain transitions in globals.css. */
const COVER_MS = 400
const HOLD_MS = 120
const REVEAL_MS = 600

const pathOf = (url: string): string => url.split(/[?#]/)[0].replace(/\/$/, '') || '/'

const labelFor = (url: string): string => {
    const path = pathOf(url)

    if (path === '/') return 'Home'
    if (path.startsWith('/work/')) return 'Case study'
    if (path.startsWith('/services/')) return 'Service'
    if (path.startsWith('/insights/')) return 'Journal'

    const labels: Record<string, string> = {
        '/work': 'Work',
        '/services': 'Services',
        '/about': 'About',
        '/insights': 'Insights',
        '/contact': 'Contact',
        '/privacy': 'Privacy policy',
        '/terms': 'Terms'
    }

    return labels[path] ?? ''
}

/*
 * A charcoal curtain that rises over the page when a visitor follows a link and lifts once the next
 * page has rendered. While it is down, html carries data-curtain so the new hero's reveals wait for it.
 * Skipped for reduced motion, hash links and links to the page already open.
 */
export const PageTransition: FC = (): ReactNode => {
    const [phase, setPhase] = useState<Phase>('idle')
    const [label, setLabel] = useState('')
    const startedAt = useRef(0)
    const timers = useRef<number[]>([])

    useEffect(() => {
        const root = document.documentElement
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')

        const clear = () => {
            timers.current.forEach((timer) => window.clearTimeout(timer))
            timers.current = []
        }

        const onStart = (url: string, options?: { shallow?: boolean }) => {
            if (options?.shallow || reduce.matches) return
            if (pathOf(url) === pathOf(Router.asPath)) return

            clear()
            startedAt.current = performance.now()
            root.dataset.curtain = 'true'
            setLabel(labelFor(url))
            setPhase('cover')
        }

        const onDone = () => {
            if (!root.dataset.curtain) return

            clear()

            const wait = Math.max(0, COVER_MS - (performance.now() - startedAt.current)) + HOLD_MS

            timers.current.push(window.setTimeout(() => {
                delete root.dataset.curtain
                setPhase('reveal')
                timers.current.push(window.setTimeout(() => setPhase('idle'), REVEAL_MS))
            }, wait))
        }

        Router.events.on('routeChangeStart', onStart)
        Router.events.on('routeChangeComplete', onDone)
        Router.events.on('routeChangeError', onDone)

        return () => {
            Router.events.off('routeChangeStart', onStart)
            Router.events.off('routeChangeComplete', onDone)
            Router.events.off('routeChangeError', onDone)
            clear()
            delete root.dataset.curtain
        }
    }, [])

    return (
        <div aria-hidden='true' data-phase={phase} className='blitz-curtain flex flex-col bg-charcoal text-cream'>
            <span className='h-1 w-full bg-coral' />

            <div className='blitz-curtain-content flex flex-1 flex-col items-center justify-center gap-5'>
                <Wordmark className='text-[clamp(2.5rem,7vw,4.5rem)]' />
                {label && (
                    <span className='flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-cream/70'>
                        <span className='h-px w-8 bg-cream/40' />
                        {label}
                        <span className='h-px w-8 bg-cream/40' />
                    </span>
                )}
            </div>

            <span className='h-1 w-full bg-coral' />
        </div>
    )
}
